import { RadialRing } from '../systems/spawning/patterns/RadialRing.js';
import { HomingShardVolley } from '../systems/spawning/patterns/HomingShardVolley.js';
import { SweepingLaser } from '../systems/spawning/patterns/SweepingLaser.js';
import { BulletBoardGrid } from '../systems/spawning/patterns/BulletBoardGrid.js';
import { TrackingBeam } from '../systems/spawning/patterns/TrackingBeam.js';
import { EchoChorus } from '../systems/spawning/patterns/EchoChorus.js';
import { AreaDenial } from '../systems/spawning/patterns/AreaDenial.js';
import { CrossBeamSweep } from '../systems/spawning/patterns/CrossBeamSweep.js';
import { Skyfall } from '../systems/spawning/patterns/Skyfall.js';
import { SovereignsWrath } from '../systems/spawning/patterns/SovereignsWrath.js';
import { Apotheosis } from '../systems/spawning/patterns/Apotheosis.js';
import { TheUnmaking } from '../systems/spawning/patterns/TheUnmaking.js';
import { GridBarrage } from '../systems/spawning/patterns/GridBarrage.js';
import { TelegraphedCharge } from '../systems/spawning/patterns/TelegraphedCharge.js';
import { Implosion } from '../systems/spawning/patterns/Implosion.js';
import { PrismMinefield } from '../systems/spawning/patterns/PrismMinefield.js';
import { SovereignSanctum } from '../systems/spawning/patterns/SovereignSanctum.js';
import { ChoirEchoStep } from '../systems/spawning/patterns/ChoirEchoStep.js';
import { ThroneDivide } from '../systems/spawning/patterns/ThroneDivide.js';
import { CorruptedCopy } from '../systems/spawning/patterns/CorruptedCopy.js';
import { SovereignsSummons } from '../systems/spawning/patterns/SovereignsSummons.js';
import { ChoralStaves } from '../systems/spawning/patterns/ChoralStaves.js';
import { EmberWake } from '../systems/spawning/patterns/EmberWake.js';
import { UnmadeGround } from '../systems/spawning/patterns/UnmadeGround.js';
import { StaccatoVolley } from '../systems/spawning/patterns/StaccatoVolley.js';
import { ConductorsBaton } from '../systems/spawning/patterns/ConductorsBaton.js';
import { GrandPause } from '../systems/spawning/patterns/GrandPause.js';
import { TheFeast } from '../systems/spawning/patterns/TheFeast.js';
import { TheRepast } from '../systems/spawning/patterns/TheRepast.js';
import { RegurgitantVolley } from '../systems/spawning/patterns/RegurgitantVolley.js';
import { JealousEmbrace } from '../systems/spawning/patterns/JealousEmbrace.js';
import { WaltzOfDaggers } from '../systems/spawning/patterns/WaltzOfDaggers.js';
import { TheVow } from '../systems/spawning/patterns/TheVow.js';

// Per-boss tuning for the shared attack building blocks — ONE ROW PER
// GAUNTLET SLOT, in BOSSES order, so the difficulty staircase reads
// straight down this array. The 2026-07 gauntlet expansion interleaved
// three new bosses between the founding four; their rows interpolate the
// neighboring tiers. Each boss opts into a small subset below so the
// common language supports identity instead of flattening every fight
// into the same attack lottery.
const ATTACK_CONFIG = [
  {
    // Slot 0 — The Hollow Sovereign.
    ringBullet: { damage: 8, speed: 80 },
    homingShard: { damage: 10, speed: 90, turnRate: 1.6 },
    laser: { tickDamage: 5, tickInterval: 0.15, width: 6, length: 9999, sweepSpeed: 1.3 },
    boardBullet: { damage: 10, speed: 55 },
    skyfall: { markerCount: 2, ringCount: 10, damage: 7, speed: 70 },
    gridBarrage: { damage: 8, cols: 5, rows: 4 },
    telegraphedCharge: { speed: 240, contactDamage: 18, width: 22 },
    implosion: { count: 12, damage: 8, speed: 120 },
  },
  {
    // Slot 1 — The Iron Cadence, a half-step past the Sovereign.
    ringBullet: { damage: 9, speed: 88 },
    homingShard: { damage: 11, speed: 98, turnRate: 1.75 },
    laser: { tickDamage: 5, tickInterval: 0.14, width: 6, length: 9999, sweepSpeed: 1.4 },
    boardBullet: { damage: 11, speed: 60 },
    skyfall: { markerCount: 2, ringCount: 11, damage: 8, speed: 75 },
    gridBarrage: { damage: 9, cols: 5, rows: 4 },
    telegraphedCharge: { speed: 260, contactDamage: 20, width: 23 },
    implosion: { count: 14, damage: 9, speed: 128 },
  },
  {
    // Slot 2 — Riven Choir.
    ringBullet: { damage: 10, speed: 95 },
    homingShard: { damage: 12, speed: 105, turnRate: 1.9 },
    laser: { tickDamage: 6, tickInterval: 0.13, width: 7, length: 9999, sweepSpeed: 1.5 },
    boardBullet: { damage: 12, speed: 65 },
    skyfall: { markerCount: 3, ringCount: 12, damage: 9, speed: 80 },
    gridBarrage: { damage: 10, cols: 5, rows: 4 },
    telegraphedCharge: { speed: 280, contactDamage: 22, width: 24 },
    implosion: { count: 16, damage: 10, speed: 135 },
  },
  {
    // Slot 3 — The Famished Court, between Choir and Throne.
    ringBullet: { damage: 11, speed: 102 },
    homingShard: { damage: 13, speed: 112, turnRate: 2.05 },
    laser: { tickDamage: 6, tickInterval: 0.12, width: 7, length: 9999, sweepSpeed: 1.6 },
    boardBullet: { damage: 13, speed: 70 },
    skyfall: { markerCount: 3, ringCount: 13, damage: 10, speed: 85 },
    gridBarrage: { damage: 11, cols: 5, rows: 5 },
    telegraphedCharge: { speed: 300, contactDamage: 24, width: 25 },
    implosion: { count: 18, damage: 11, speed: 142 },
  },
  {
    // Slot 4 — Ashen Throne.
    ringBullet: { damage: 12, speed: 110 },
    homingShard: { damage: 14, speed: 120, turnRate: 2.2 },
    laser: { tickDamage: 7, tickInterval: 0.11, width: 8, length: 9999, sweepSpeed: 1.7 },
    boardBullet: { damage: 14, speed: 75 },
    skyfall: { markerCount: 4, ringCount: 14, damage: 11, speed: 90 },
    gridBarrage: { damage: 12, cols: 6, rows: 5 },
    telegraphedCharge: { speed: 320, contactDamage: 26, width: 26 },
    implosion: { count: 20, damage: 12, speed: 150 },
  },
  {
    // Slot 5 — The Jealous Regent, between Throne and King.
    ringBullet: { damage: 13, speed: 118 },
    homingShard: { damage: 15, speed: 128, turnRate: 2.35 },
    laser: { tickDamage: 7, tickInterval: 0.105, width: 8, length: 9999, sweepSpeed: 1.8 },
    boardBullet: { damage: 15, speed: 80 },
    skyfall: { markerCount: 4, ringCount: 15, damage: 12, speed: 95 },
    gridBarrage: { damage: 13, cols: 6, rows: 5 },
    telegraphedCharge: { speed: 340, contactDamage: 28, width: 27 },
    implosion: { count: 22, damage: 13, speed: 158 },
  },
  {
    // Slot 6 — The Unmade King. Harshest tier, one full step up from the Regent.
    ringBullet: { damage: 14, speed: 125 },
    homingShard: { damage: 16, speed: 135, turnRate: 2.5 },
    laser: { tickDamage: 8, tickInterval: 0.1, width: 9, length: 9999, sweepSpeed: 1.9 },
    boardBullet: { damage: 16, speed: 85 },
    skyfall: { markerCount: 5, ringCount: 16, damage: 13, speed: 100 },
    gridBarrage: { damage: 14, cols: 6, rows: 5 },
    telegraphedCharge: { speed: 360, contactDamage: 30, width: 28 },
    implosion: { count: 24, damage: 14, speed: 165 },
  },
];

// The Unmade King's tier — heralds pull their pattern damage from here (see
// MINIBOSSES below) regardless of which boss they visually pair with.
const FINAL_TIER = ATTACK_CONFIG[ATTACK_CONFIG.length - 1];

const SHARED_PATTERN_BUILDERS = {
  ring: (c) => new RadialRing({ config: c.ringBullet }),
  homing: (c) => new HomingShardVolley({ config: c.homingShard }),
  laser: (c) => new SweepingLaser({ config: c.laser }),
  board: (c) => new BulletBoardGrid({ config: c.boardBullet }),
  skyfall: (c) => new Skyfall({ config: c.skyfall }),
  grid: (c) => new GridBarrage({ minPhase: 2, config: c.gridBarrage }),
  charge: (c) => new TelegraphedCharge({ minPhase: 0, config: c.telegraphedCharge }),
  implosion: (c) => new Implosion({ minPhase: 1, config: c.implosion }),
};

function createSharedPatterns(config, names) {
  return names.map((name) => SHARED_PATTERN_BUILDERS[name](config));
}

/**
 * The seven-boss gauntlet a run fights in sequence, each preceded by a wave
 * stage (see Game.js's gauntletIndex/_startBossStage/_onBossDied). Physical
 * size and phase-gating thresholds are shared (BOSS.radius/phaseThresholds
 * in config/constants.js); everything that escalates fight-to-fight lives
 * here — HP, speed, attack-layering pace, per-pattern damage/speed, wave
 * composition, and each boss's exclusive pattern(s) + desperation finisher.
 *
 * 2026-07 expansion: three "interloper" bosses (The Iron Cadence, The
 * Famished Court, The Jealous Regent) are interleaved between the founding
 * court — each built around a rule no other fight uses (beat-locked time,
 * a devour economy, punished distance) rather than a config retune. They
 * do not send heralds to the pre-King gauntlet (see MINIBOSSES below) and
 * The Unmade King remains the sole final boss.
 *
 * HP curve (ACT-PARITY A17, solved against the bench rather than argued):
 * 1900 -> 58000, a flat **~1.76x per stage**.
 *
 * Two earlier passes moved this number by reasoning about it — ~1.25x, then
 * ~1.30x "because player DPS compounds faster than that" — and both were right
 * about the direction and wrong about the size, because nothing measured it.
 * `pacingBench.js` now does. Per-boss time-to-kill down this array WAS:
 *
 *     73  46  37  31  33  27  14 seconds
 *
 * The Unmade King was the shortest fight in the act by five times: the act's
 * difficulty ran backwards in the only unit a player experiences. 1.30x sounds
 * steep and is nowhere near a build that gains weapons, levels, a multiplier
 * stack and a fusion over seven fights. It is now:
 *
 *     52  41  40  48  62  51  40 seconds
 *
 * — no longer a collapse. The residual scatter (that 62) is deck variance that
 * survives a twelve-seed median, not a shape; see `benchCurve`.
 *
 * How it was solved, because the method matters more than the seven numbers:
 * a damped fixed-point iteration against a FIXED seed panel, projecting onto a
 * two-parameter geometric fit each pass. Free-solving all seven against six
 * seeds converged beautifully and then disagreed with the twelve-seed panel by
 * 20 seconds a boss — it had fitted the decks, not the curve. Two parameters
 * cannot do that. If you retune this, retune `base` and `ratio`.
 *
 * The opener moved too (3000 -> 1900), which reverses the old instruction to
 * leave it alone. That instruction existed to stop the first fight becoming a
 * slog, and at 73 seconds against one weapon it already was one.
 *
 * THE KING IS NOT ON THE GEOMETRIC CURVE, and the exception is the point.
 * 1.76x would put him at 58000, where he measures 50s against a mid-act peak
 * of 62s — the act's finale would be its third-shortest fight. At 82000 he is
 * 70s, the longest fight in Act I, which is what a finale should be.
 *
 * Why the curve cannot reach that on its own: player DPS does not grow at a
 * constant rate. It grows roughly 2.28x, 1.78x, 1.49x, 1.36x, 2.13x, 1.83x a
 * fight — fast early while weapons arrive, slowest mid-act where
 * CombatSystem's soft cap bites, fast again as a fusion lands. A single ratio
 * cannot track a U-shape, so time-to-kill sags wherever DPS outruns 1.76x.
 * Part of the finale's correction also pays for the herald gate, which hands
 * over ~42000 HP worth of damage XP immediately beforehand: isolated, that
 * gate costs the King 14% of his fight (70s with it, 81s without), where Act
 * II's and Act III's gates cost theirs nothing measurable.
 *
 * One exception with a named cause is not the same as seven free parameters.
 * A held-out twelve-seed panel, disjoint from SEED_PANEL, puts this at 1.32x
 * finale/opener against the panel's 1.34x. Free-solving every boss does NOT
 * survive that test — panel and holdout disagree on implied DPS by up to 23% a
 * boss, which is exactly the overfit the two-parameter fit exists to avoid.
 * Add an exception only where you can name the discontinuity it pays for.
 *
 * `layerFromPhase` staircase (A9): 3, 3, 2, 2, 1, 1, 0 — pattern layering
 * arrives one phase earlier every two bosses, so the King is stacking attacks
 * from the first second while the Sovereign only ever does it at the end.
 * This is an EVERY-ACT device, not an Act I one. It was Act I's alone by
 * accident: Act II and Act III were each authored as a flat 2 with a finale at
 * 1, which is a difficulty *level* rather than a difficulty *slope*, and it
 * left both acts with no escalation in the one axis Act I escalates hardest.
 * Both now descend to 0 at their finale like this one does (Act II
 * 2,2,2,1,1,0; Act III 2,2,1,1,1,0 — one boss earlier, because its player
 * starts eight `actStartLevel` levels stronger). The pattern is: every act
 * ends with a boss that never gives the player an unlayered phase.
 */
// Every def carries a `subtitle`, the same as Act II's, Act III's and the
// challenge exclusives'. Boss Challenge's cards fall back to a tier numeral
// when one is missing (BossSelect.tierLabel), and Act I's seven were the only
// cards in the game still reading "Tier IV" beside cards reading
// "Act II · Undersong". The fallback stays — it is the right thing for a def
// that genuinely has no act — but nothing in the campaign relies on it now.
// **2026-07-27 act-rate pass: every HP value below is 0.80x what it was**
// (1900/3400/6000/10600/18700/32900/82000, and the heralds' 10500).
//
// Uniform on purpose. ACT-PARITY spent real effort on the *shape* of this
// ladder — the >=1.5x step between consecutive bosses, the King's 2.5x
// climax, the gate summing to less than the King — and a uniform scalar
// leaves every one of those ratios untouched while moving the only thing
// that was wrong, which was the absolute.
//
// What was wrong, from pacingBench's seed panel:
//
//              boss time    wave time   total      per-boss TTK
//   Act I         360s         189s     9.7 min    52 41 40 48 62 51 67
//   Act II        162s          81s     5.0 min    28 19 21 26 32 38
//   Act III       183s          79s     4.4 min    24 26 28 32 35 38
//
// Act I is the on-ramp, the only act a new player can enter, and it ran
// **more than twice the length of the endgame** — with an opening fight
// (52s) longer than Act III's finale (38s). Not because its bosses are
// tankier in the abstract, but because a level-1 player with one weapon is
// fighting them; Acts II and III start elevated with a built kit. The HP
// numbers were authored against the boss, not against who is holding the
// controller when they meet it.
//
// This brings Act I's boss time to ~290s and the act to ~8.2 min: still the
// longest act, which it should be at seven bosses against six, but no longer
// a different order of commitment. The other half of the same ratio is
// ECHO_BONUS_PER_BOSS / SHARDS_PER_BOSS (Currency.js), which were paying Act
// I a fifth of the later acts' rate per minute.
export const BOSSES = [
  {
    name: 'The Hollow Sovereign',
    subtitle: 'Act I · Gauntlet',
    arenaTheme: { floor: '#15151f', line: '#3a3a52', accent: '#ff5d6c' },
    spriteKey: 'hollowSovereign',
    musicTheme: 'hollowSovereign', // cosmetic only — see data/musicThemes.js

    // BOSS-REWORK.md §B4 — the court's centre never chases. It holds the
    // middle and makes the arena come to it, which is also what its Sanctum
    // (a rotating safe wedge around the boss) was always authored against.
    movement: 'anchor',
    // §B6 — the Sovereign's Sanctum is the first card in the game you can
    // ANSWER rather than merely survive: pour 6% of its pool into it while it
    // runs and it breaks early, which counts as a capture. The fraction is of
    // the boss's own HP, so the same line reads correctly at 1520 and 65600.
    cardBreak: { fraction: 0.06, cards: ['Sovereign Sanctum', "Sovereign's Summons"] },
    maxHp: 1520,
    moveSpeed: 30,
    // §B1 — the first fight in the game teaches in a fixed order: the ring
    // (read the gaps), then the sanctum (read a rule), then the charge (read
    // the boss itself). After that the pool takes over, so the second clear
    // isn't a memorisation test.
    script: [
      ['Radial Ring', 'Sovereign Sanctum', 'Telegraphed Charge'],
      ["Sovereign's Summons", 'Sweeping Laser'],
    ],
    layerFromPhase: 3,
    pickCooldownMultiplier: 1,
    createPatterns: () => [
      ...createSharedPatterns(ATTACK_CONFIG[0], ['ring', 'laser', 'charge', 'implosion']),
      new TrackingBeam({ config: { turnRate: 0.15, tickDamage: 6, tickInterval: 0.15, width: 7, length: 9999 } }),
      new SovereignSanctum({ config: { rotationSpeed: 0.3, innerDangerRadius: 28, damage: 8, shockwaveSpeed: 75, shockwaveDamage: 7 } }),
      // Signature: the court teleports YOU (dash on the beat to slip it).
      new SovereignsSummons({ minPhase: 1, config: { ringSpeed: 70, ringDamage: 9, volleySpeed: 240, volleyDamage: 12 } }),
    ],
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 8,
      minionHp: 30,
      minionMoveSpeed: 68,
      minionContactDamage: 8,
      minionShotDamage: 4,
      minionShotSpeed: 70,
      minionFireInterval: 2.4,
      // minionTypes intentionally omitted — Game.js's _waveConfig rolls a
      // fresh random mix per wave stage (see MinionWaveSystem's
      // randomMinionTypeSet) instead of a fixed curated list.
    },
  },
  {
    // Interloper: the court's own conductor, gone tyrant. Everything it
    // does is quantized to an audible/visible beat — bullets advance in
    // lockstep lurches (Staccato Volley), its beam strikes only on grid
    // angles it previewed (Conductor's Baton), and its signature caesura
    // freezes every bullet on screen before releasing them all at surge
    // speed (Grand Pause). The fight teaches rhythm-reading early in the
    // gauntlet: dodge on the rests, commit on the downbeats.
    name: 'The Iron Cadence',
    subtitle: 'Act I · Gauntlet',
    arenaTheme: { floor: '#12151c', line: '#39445c', accent: '#9fb8e8' },
    spriteKey: 'ironCadence',
    musicTheme: 'ironCadence',

    // §B4/§B8 — quantized time, in the body as well as the bullets: it steps
    // to the mirror of wherever the player stands (a metronome's other arm)
    // and its wind-ups are the tightest and most regular in the game.
    movement: 'mirror',
    telegraphScale: 0.8,
    cardBreak: { fraction: 0.06, cards: ['Grand Pause'] },
    maxHp: 2720,
    moveSpeed: 33,
    script: [
      ['Staccato Volley', "Conductor's Baton"],
      ['Grand Pause', 'Staccato Volley'],
    ],
    layerFromPhase: 3,
    pickCooldownMultiplier: 0.95,
    createPatterns: () => [
      ...createSharedPatterns(ATTACK_CONFIG[1], ['ring', 'board', 'charge']),
      new StaccatoVolley({ config: { beatInterval: 0.62, hopFraction: 0.45, damage: 9, speed: 74, ringCount: 12 } }),
      new ConductorsBaton({ config: { tickDamage: 6, tickInterval: 0.15, width: 7 } }),
      // Signature: the caesura — every bullet on screen freezes, then surges.
      new GrandPause({ minPhase: 1, config: { holdDuration: 1.35, surgeMultiplier: 1.55 } }),
    ],
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 9,
      minionHp: 36,
      minionMoveSpeed: 74,
      minionContactDamage: 9,
      minionShotDamage: 4,
      minionShotSpeed: 75,
      minionFireInterval: 2.25,
      // minionTypes intentionally omitted — see Hollow Sovereign above.
    },
  },
  {
    name: 'Riven Choir',
    subtitle: 'Act I · Gauntlet',
    arenaTheme: { floor: '#171326', line: '#523a68', accent: '#c084fc' },
    spriteKey: 'rivenChoir',
    musicTheme: 'rivenChoir',

    // §B4 — a choir sings from the far side of the hall. It keeps its
    // distance, which is also what makes its echo cards land as answers to
    // where the player *was*.
    movement: 'keepAway',
    movementConfig: { preferredRange: 190 },
    // §B7 — the Choir is Act I's polarity teacher: whole cards arrive warm or
    // cold, alternating, so attunement has something to read long before the
    // Antiphon's finale.
    tonedCards: { mismatchBonus: 0.25 },
    cardBreak: { fraction: 0.06, cards: ['Choral Staves'] },
    maxHp: 4800,
    moveSpeed: 36,
    script: [
      ['Choral Staves', 'Echo Chorus'],
      ['Echo Step', 'Skyfall'],
    ],
    layerFromPhase: 2,
    pickCooldownMultiplier: 0.85,
    createPatterns: () => [
      ...createSharedPatterns(ATTACK_CONFIG[2], ['ring', 'homing', 'board', 'skyfall']),
      new EchoChorus({ config: { echoCount: 3, ringCount: 16, damage: 8, speed: 85 } }),
      new ChoirEchoStep({ config: { damage: 9, speed: 92 } }),
      // Signature: the arena becomes sheet music — stand on the resting staves.
      new ChoralStaves({ config: { lines: 5, bandHalf: 14, restCount: 2, pulseDamage: 10 } }),
    ],
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 10,
      minionHp: 42,
      minionMoveSpeed: 80,
      minionContactDamage: 10,
      minionShotDamage: 5,
      minionShotSpeed: 80,
      minionFireInterval: 2.1,
      // minionTypes intentionally omitted — see Hollow Sovereign above.
    },
  },
  {
    // Interloper: a court that never stopped eating. The only boss in the
    // game that HEALS — its signature Feast marches courtiers toward the
    // maw and every one that arrives restores HP and fattens later
    // volleys (gorged stacks, see RegurgitantVolley), so the fight is
    // target triage, not just dodging. The Repast inverts bullet clutter:
    // everything it has in flight gets inhaled and re-served as aimed
    // fire — but overeating opens the game's most generous reward window.
    name: 'The Famished Court',
    subtitle: 'Act I · Gauntlet',
    arenaTheme: { floor: '#15130c', line: '#4f4a28', accent: '#cdd45a' },
    spriteKey: 'famishedCourt',
    musicTheme: 'famishedCourt',

    // §B4/§B8 — it comes for you, slowly and inevitably, and every wind-up is
    // longer than anything else in the act. Dread, not speed.
    movement: 'stalk',
    movementConfig: { standoff: 90 },
    telegraphScale: 1.3,
    cardBreak: { fraction: 0.06, cards: ['The Repast'] },
    maxHp: 8480,
    moveSpeed: 38,
    script: [
      ['The Feast', 'Regurgitant Volley'],
      ['The Repast', 'The Feast'],
    ],
    layerFromPhase: 2,
    pickCooldownMultiplier: 0.8,
    createPatterns: () => [
      ...createSharedPatterns(ATTACK_CONFIG[3], ['homing', 'skyfall', 'implosion']),
      // Signature: courtiers march to the maw — each one devoured heals the
      // Court and stacks `gorged`. Kill them first.
      new TheFeast({ config: { count: 4, courtierHp: 60, courtierSpeed: 34, healPerCourtier: 130, contactDamage: 8 }, combat: null }),
      new TheRepast({ minPhase: 1, config: { baseSpits: 3, spitSpeed: 165, spitDamage: 11, overeatThreshold: 4 } }),
      new RegurgitantVolley({ config: { baseArcs: 2, pellets: 5, speed: 68, damage: 11 } }),
    ],
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 11,
      minionHp: 48,
      minionMoveSpeed: 86,
      minionContactDamage: 11,
      minionShotDamage: 5,
      minionShotSpeed: 85,
      minionFireInterval: 1.95,
      // minionTypes intentionally omitted — see Hollow Sovereign above.
    },
  },
  {
    name: 'Ashen Throne',
    subtitle: 'Act I · Gauntlet',
    arenaTheme: { floor: '#21151a', line: '#69402e', accent: '#ff9f43' },
    spriteKey: 'ashenThrone',
    musicTheme: 'ashenThrone',

    // §B4 — a throne sits on its throne. Everything dangerous about this
    // fight is the floor, not the body; the def's own comments said so long
    // before the entity did.
    movement: 'anchor',
    cardBreak: { fraction: 0.06, cards: ['Ember Wake'] },
    maxHp: 14960,
    moveSpeed: 42,
    script: [
      ['Ember Wake', 'Area Denial'],
      ['Throne Divide', 'Prism Minefield'],
    ],
    layerFromPhase: 1,
    pickCooldownMultiplier: 0.7,
    createPatterns: () => [
      ...createSharedPatterns(ATTACK_CONFIG[4], ['ring', 'grid', 'implosion']),
      new AreaDenial({ config: { zoneCount: 4, zoneRadius: 45, damage: 16 } }),
      new CrossBeamSweep({ config: { tickDamage: 8, tickInterval: 0.2, thickness: 14 } }),
      new PrismMinefield({ config: { count: 4, radius: 28, bulletCount: 8, damage: 12, speed: 85 } }),
      new ThroneDivide({ config: { width: 18, gapSize: 62, damage: 14 } }),
      // Signature: your own footsteps ignite — don't backtrack.
      new EmberWake({ config: { sampleInterval: 0.45, igniteDelay: 0.7, patchRadius: 14, patchDuration: 2.6, tickDamage: 7 } }),
    ],
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 12,
      minionHp: 54,
      minionMoveSpeed: 92,
      minionContactDamage: 12,
      minionShotDamage: 6,
      minionShotSpeed: 90,
      minionFireInterval: 1.8,
      // minionTypes intentionally omitted — see Hollow Sovereign above.
    },
  },
  {
    // Interloper: the throne's spurned consort — the anti-kiting boss.
    // Its signature Embrace drags a straying player back toward it with
    // yearning ticks, the Waltz walls the arena into inside/outside with
    // an orbiting blade ring, and the Vow pins the player to a spot of
    // their own choosing (hold it for a reward window, break it early and
    // eat lances). The whole game trains "keep your distance" — this
    // fight, alone, punishes exactly that.
    name: 'The Jealous Regent',
    subtitle: 'Act I · Gauntlet',
    arenaTheme: { floor: '#1c1016', line: '#5c2e44', accent: '#ff87b3' },
    spriteKey: 'jealousRegent',
    musicTheme: 'jealousRegent',

    // §B4 — the anti-kiting boss finally moves like one. It closes, always,
    // and stops just short: the Embrace drags you back, the body makes sure
    // there was never far to drag.
    movement: 'stalk',
    movementConfig: { standoff: 55 },
    cardBreak: { fraction: 0.06, cards: ['Jealous Embrace', 'The Vow'] },
    maxHp: 26320,
    moveSpeed: 46,
    script: [
      ['Jealous Embrace', 'Waltz of Daggers'],
      ['The Vow', 'Jealous Embrace'],
    ],
    layerFromPhase: 1,
    pickCooldownMultiplier: 0.68,
    createPatterns: () => [
      ...createSharedPatterns(ATTACK_CONFIG[5], ['ring', 'laser', 'implosion']),
      // Signature: the devotion ring — outside it you are dragged back in.
      new JealousEmbrace({ config: { embraceRadius: 120, pullStrength: 55, yearnDamage: 7, ringCount: 10, ringSpeed: 86, ringDamage: 11 } }),
      new WaltzOfDaggers({ config: { daggerCount: 14, orbitRadius: 68, orbitOmega: 1.1, damage: 12, flingSpeed: 150 } }),
      new TheVow({ minPhase: 1, config: { allowRadius: 52, holdDuration: 2.4, lanceCount: 8, lanceSpeed: 250, lanceDamage: 13, testRingSpeed: 78, testRingDamage: 11 } }),
    ],
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 13,
      minionHp: 57,
      minionMoveSpeed: 96,
      minionContactDamage: 13,
      minionShotDamage: 6,
      minionShotSpeed: 95,
      minionFireInterval: 1.7,
      // minionTypes intentionally omitted — see Hollow Sovereign above.
    },
  },
  {
    // The true final boss — reached via a warning screen after The Jealous
    // Regent dies (see Game.js's _onBossDied/FinalWarningOverlay), not an
    // instant transition like the others. `spriteKeyPhase2`/`hasPulseGlow`
    // are only read for this entry; every other boss leaves them unset and
    // renders exactly as before.
    name: 'The Unmade King',
    subtitle: 'Act I · Finale',
    arenaTheme: { floor: '#100d18', line: '#4d263d', accent: '#ff3b6b' },
    spriteKey: 'unmadeKing',
    musicTheme: 'unmadeKing',
    spriteKeyPhase2: 'unmadeKingPhase2',
    isFinalBoss: true,
    hasPulseGlow: true,
    // §B4/§B8 — the King mirrors: whatever half of the arena the player
    // takes, he takes the other, and the floor between them is the fight.
    // Slightly tighter wind-ups than the act's baseline — the finale should
    // read faster than everything that led to it.
    movement: 'mirror',
    telegraphScale: 0.9,
    cardBreak: { fraction: 0.05, cards: ['Unmade Ground', 'Apotheosis'] },
    maxHp: 65600,
    moveSpeed: 50,
    script: [
      ['Unmade Ground', "Sovereign's Wrath"],
      ['Corrupted Copy', 'Prism Minefield'],
      ['Apotheosis', 'Unmade Ground'],
    ],
    layerFromPhase: 0, // patterns layer from the very first second — no grace period at all
    pickCooldownMultiplier: 0.6,
    createPatterns: () => [
      ...createSharedPatterns(ATTACK_CONFIG[6], ['homing', 'laser', 'grid', 'implosion']),
      new SovereignsWrath({ config: { ringCount: 14, damage: 10, speed: 100, cornerMargin: 40 } }),
      new PrismMinefield({ minPhase: 0, config: { count: 5, radius: 30, bulletCount: 10, damage: 14, speed: 100 } }),
      new CorruptedCopy({ config: { damage: 12, speed: 115 }, combat: null }),
      // Signature: the King deletes the floor — void cells you cannot stand in.
      new UnmadeGround({ config: { cols: 6, rows: 4, voidFraction: 0.35, tickDamage: 9 } }),
    ],
    // Only unlocked once the boss drops below 50% HP — see SpawningSystem's
    // phase2Pattern handling. Its "true form" gains a move nothing else has.
    createPhase2Pattern: () => new Apotheosis({ config: { burstCount: 5, burstInterval: 0.45, ringCount: 20, damage: 12, speed: 130 } }),
    // Final all-out death phase: the safe pocket still collapses, but the King
    // spends the full window firing escalating rings, aimed volleys, and
    // collapse pulses instead of repeating one empty one-shot telegraph.
    createDesperationPattern: () => new TheUnmaking({ config: {
      startRadius: 220,
      openingRadius: 170,
      endRadius: 40,
      pulseDamage: 36,
      ringCount: 18,
      finalRingCount: 32,
      ringSpeed: 78,
      finalRingSpeed: 132,
      ringDamage: 12,
      volleyCount: 2,
      finalVolleyCount: 5,
      volleySpeed: 118,
      finalVolleySpeed: 170,
      volleyDamage: 14,
      ringInterval: 0.86,
      finalRingInterval: 0.52,
      volleyInterval: 1.55,
      finalVolleyInterval: 0.82,
      pulseInterval: 1.55,
      finalPulseInterval: 0.9,
    } }),
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 14,
      minionHp: 60,
      minionMoveSpeed: 100,
      minionContactDamage: 14,
      minionShotDamage: 7,
      minionShotSpeed: 100,
      minionFireInterval: 1.6,
      // minionTypes intentionally omitted — see Hollow Sovereign above.
    },
  },
];

// Builds a lightweight herald encounter. All four now fight back-to-back in
// the wave stage right before the true final boss (see Game.js's
// _startMinibossStage/_spawnMinibossFromQueue/_onMinibossDied) rather than
// one per gauntlet stage — a last gauntlet-within-the-gauntlet leading into
// the Unmade King. Spread-inherits the real boss's spriteKey/arenaTheme/
// moveSpeed etc. (it visually reads as a smaller version of what's coming,
// which is the point — no separate sprite art needed) but overrides name,
// maxHp, and pattern set: a single telegraphed attack (explicit minPhase: 0
// so it's always pickable, since several of these pattern classes default
// to minPhase 1) instead of the real boss's full rotation, layerFromPhase
// pushed out of reach so patterns never stack, and no phase2/desperation
// pattern so it's a short, bounded fight rather than a second full boss.
function createMiniboss(bossDef, { name, maxHp, createPattern }) {
  return {
    ...bossDef,
    name,
    maxHp,
    layerFromPhase: 99,
    pickCooldownMultiplier: 1,
    isMiniboss: true,
    // A factory, like every real boss's createPatterns above — not a
    // pre-built instance — so endless mode (which loops back through the
    // same 4 minibosses repeatedly within one run) gets a fresh, un-mutated
    // pattern each time instead of reusing one stateful instance across
    // encounters.
    createPatterns: () => [createPattern()],
    createPhase2Pattern: undefined,
    createDesperationPattern: undefined,
  };
}

// All four share one HP value and pull their pattern damage from the final
// boss's tier (FINAL_TIER) instead of their paired boss's tier, so every
// herald hits roughly as hard as the final one.
//
// HERALD GATE WEIGHT (A8, re-derived under A17): 7000 each. The gate in front of
// each act's finale, measured against that finale's own HP, was inverted —
// Act I 4 x 6500 = 26000 against a 17000 King (1.53x), where Act II runs 0.71x
// and Act III 0.56x. The longest gauntlet-within-the-gauntlet sat in front of
// the WEAKEST finale, in the act built as the on-ramp; the four heralds were
// simply never re-examined after the King's HP moved to 17000 in the 2026-07
// balance pass. The count stays at four — the founding court's honor guard is
// the fiction, and cutting one to hit a number would spend that fiction on
// arithmetic — so the ratio comes out of per-herald HP instead.
//
// The number is now set in TIME, which is A8's actual lesson: an HP ratio is
// only a difficulty ratio where DPS is flat across the comparison, and across
// a run it is not. A8 first tried 3000 each to hit 0.71x of the King's HP and
// landed at 1.15x of his time — still the longest approach in the game leading
// into the shortest fight. At 7000 each against the A17 King the gate is 27s
// against his 40s, or **0.68x in time**, which sits between Act II's 0.73x and
// Act III's 0.63x. Measured, not derived.
//
// TWO CORRECTIONS, recorded because both wrong versions were committed:
//
//  1. "Cutting the gate moves the King's TTK by one second" was measured
//     against the OLD 17000 King, who melted in ~14s with or without a gate —
//     a floor effect, not a relationship.
//  2. "The gate costs the finale 31%" was a BENCH BUG. pacingBench ran gate
//     dummies as boss-tier, so each herald emitted `boss:died` and handed the
//     simulated player a guaranteed level plus XP_PER_BOSS_KILL. Real heralds
//     emit `minion:died` + `miniboss:died` (see Boss.finishDying) and grant
//     neither. Four phantom levels, landing immediately before the finale —
//     the worst possible place for a measurement error. Corrected, the gate
//     costs the King 14%.
//
// The gate stands, and here is what it actually is, because this paragraph
// used to close on "the 10500 stands" while arguing 7000 above it and while
// MINIBOSSES below read 8400 — three numbers, no two of them the same, and
// anyone retuning the gate off that comment was working from a premise that
// matched nothing (BOSS-REWORK.md's errata note).
//
// Reconciled: 10500 was the pre-scalar per-herald HP, 7000 was the number the
// reasoning above argued toward, and the 2026-07-27 act-rate pass then
// multiplied every HP in this file — the heralds included — by 0.80. What ships
// is **8400 a herald**, which is 10500 x 0.80. The RATIO is what was solved and
// what survives all of it: a gate worth ~0.65x of the finale's time, measuring
// 0.63x with the bench bug fixed. Retune the ratio, then read the HP off it.
//
// Deliberately still FOUR heralds — the founding court's only. The three
// interlopers (Iron Cadence / Famished Court / Jealous Regent) send none:
// the heralds are the KING'S honor guard, and three more pairs would drag
// an already-long finale. Game.js's herald-queue math is length-agnostic
// (see _minibossDefForIndex/_spawnMinibossFromQueue), so this list and
// BOSSES no longer need to stay the same length.
const HERALD_SOURCES = {
  hollowSovereign: BOSSES.find((b) => b.spriteKey === 'hollowSovereign'),
  rivenChoir: BOSSES.find((b) => b.spriteKey === 'rivenChoir'),
  ashenThrone: BOSSES.find((b) => b.spriteKey === 'ashenThrone'),
  unmadeKing: BOSSES.find((b) => b.spriteKey === 'unmadeKing'),
};

export const MINIBOSSES = [
  createMiniboss(HERALD_SOURCES.hollowSovereign, {
    name: "Sovereign's Herald",
    maxHp: 8400,
    createPattern: () => new RadialRing({ minPhase: 0, config: FINAL_TIER.ringBullet }),
  }),
  createMiniboss(HERALD_SOURCES.rivenChoir, {
    name: 'Choir Fragment',
    maxHp: 8400,
    createPattern: () => new HomingShardVolley({ minPhase: 0, config: FINAL_TIER.homingShard }),
  }),
  createMiniboss(HERALD_SOURCES.ashenThrone, {
    name: 'Throne Vanguard',
    maxHp: 8400,
    createPattern: () => new SweepingLaser({ minPhase: 0, config: FINAL_TIER.laser }),
  }),
  createMiniboss(HERALD_SOURCES.unmadeKing, {
    name: "King's Harbinger",
    maxHp: 8400,
    createPattern: () => new TelegraphedCharge({ minPhase: 0, config: FINAL_TIER.telegraphedCharge }),
  }),
];
